import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { ShortContentItem } from '@/hooks/useShortContent';
import { format } from 'date-fns';

/**
 * Export a single travel byte to PDF
 */
export const exportTravelByteToPDF = (item: ShortContentItem) => {
  try {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    let yPosition = margin;

    // Title
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(item.title, margin, yPosition);
    yPosition += 12;

    // Content type badge
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text(`Type: ${item.content_type.toUpperCase()}`, margin, yPosition);
    yPosition += 10;

    // Metadata line
    const metadata: string[] = [];
    if (item.category) metadata.push(`Category: ${item.category}`);
    if (item.status) metadata.push(`Status: ${item.status}`);
    if (item.is_featured) metadata.push('Featured');
    
    if (metadata.length > 0) {
      doc.text(metadata.join(' • '), margin, yPosition);
      yPosition += 10;
    }

    // Content
    yPosition += 5;
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    const splitContent = doc.splitTextToSize(item.content, pageWidth - 2 * margin);
    doc.text(splitContent, margin, yPosition);
    yPosition += splitContent.length * 5 + 15;

    // Additional details section
    if (yPosition < doc.internal.pageSize.getHeight() - 60) {
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 10;

      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      
      if (item.source) {
        doc.text(`Source: ${item.source}`, margin, yPosition);
        yPosition += 7;
      }
      
      doc.text(`Views: ${item.view_count}`, margin, yPosition);
      yPosition += 7;
      
      doc.text(`Created: ${format(new Date(item.created_at), 'MMM d, yyyy')}`, margin, yPosition);
      yPosition += 7;
      
      if (item.publish_date) {
        doc.text(`Published: ${format(new Date(item.publish_date), 'MMM d, yyyy')}`, margin, yPosition);
      }
    }

    // Save the PDF
    const filename = `travel_byte_${item.slug || item.id}.pdf`;
    doc.save(filename);
  } catch (error) {
    console.error('Error exporting travel byte PDF:', error);
    throw error;
  }
};

/**
 * Export multiple travel bytes to a single PDF report
 */
export const exportTravelBytesToPDF = (items: ShortContentItem[]) => {
  try {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    let yPosition = margin;

    // Title
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('Travel Bytes Export', margin, yPosition);
    yPosition += 15;

    // Summary stats
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text(`Generated: ${format(new Date(), 'MMM d, yyyy h:mm a')}`, margin, yPosition);
    doc.text(`Total Items: ${items.length}`, pageWidth - margin - 40, yPosition);
    yPosition += 10;

    // Stats breakdown
    const typeBreakdown = items.reduce((acc, item) => {
      acc[item.content_type] = (acc[item.content_type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const statusBreakdown = items.reduce((acc, item) => {
      acc[item.status] = (acc[item.status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.text(`By Type: ${Object.entries(typeBreakdown).map(([k, v]) => `${k}: ${v}`).join(', ')}`, margin, yPosition);
    yPosition += 7;
    doc.text(`By Status: ${Object.entries(statusBreakdown).map(([k, v]) => `${k}: ${v}`).join(', ')}`, margin, yPosition);
    yPosition += 15;

    // Create table data
    const tableData = items.map(item => [
      item.title.length > 40 ? item.title.substring(0, 37) + '...' : item.title,
      item.content_type,
      item.category || '-',
      item.status,
      item.view_count.toString(),
      format(new Date(item.created_at), 'MMM d, yyyy')
    ]);

    // Add table
    autoTable(doc, {
      head: [['Title', 'Type', 'Category', 'Status', 'Views', 'Created']],
      body: tableData,
      startY: yPosition,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [59, 130, 246] },
      columnStyles: {
        0: { cellWidth: 60 },
        1: { cellWidth: 25 },
        2: { cellWidth: 30 },
        3: { cellWidth: 25 },
        4: { cellWidth: 20 },
        5: { cellWidth: 30 }
      }
    });

    // Save the PDF
    const filename = `travel_bytes_batch_${format(new Date(), 'yyyy-MM-dd')}.pdf`;
    doc.save(filename);
  } catch (error) {
    console.error('Error exporting travel bytes batch PDF:', error);
    throw error;
  }
};

/**
 * Export travel bytes to CSV format
 */
export const exportTravelBytesToCSV = (items: ShortContentItem[], selectedFields?: Set<string>) => {
  try {
    // Field mapping
    const fieldMap: Record<string, { header: string; getValue: (item: ShortContentItem) => string | number }> = {
      id: { header: 'ID', getValue: (item) => item.id },
      title: { header: 'Title', getValue: (item) => `"${item.title.replace(/"/g, '""')}"` },
      content: { header: 'Content', getValue: (item) => `"${item.content.replace(/"/g, '""')}"` },
      content_type: { header: 'Type', getValue: (item) => item.content_type },
      category: { header: 'Category', getValue: (item) => item.category || '' },
      status: { header: 'Status', getValue: (item) => item.status },
      is_featured: { header: 'Featured', getValue: (item) => item.is_featured ? 'Yes' : 'No' },
      view_count: { header: 'Views', getValue: (item) => item.view_count },
      source: { header: 'Source', getValue: (item) => item.source || '' },
      slug: { header: 'Slug', getValue: (item) => item.slug || '' },
      image_url: { header: 'Image URL', getValue: (item) => item.image_url || '' },
      created_at: { header: 'Created', getValue: (item) => format(new Date(item.created_at), 'yyyy-MM-dd HH:mm:ss') },
      updated_at: { header: 'Updated', getValue: (item) => format(new Date(item.updated_at), 'yyyy-MM-dd HH:mm:ss') },
      publish_date: { header: 'Publish Date', getValue: (item) => item.publish_date ? format(new Date(item.publish_date), 'yyyy-MM-dd HH:mm:ss') : '' },
      author_id: { header: 'Author ID', getValue: (item) => item.author_id || '' },
      display_order: { header: 'Display Order', getValue: (item) => item.display_order?.toString() || '' },
    };

    // Determine which fields to include
    const fieldsToInclude = selectedFields && selectedFields.size > 0
      ? Array.from(selectedFields)
      : Object.keys(fieldMap);

    // Define CSV headers based on selected fields
    const headers = fieldsToInclude.map(key => fieldMap[key]?.header || key);

    // Convert items to CSV rows
    const rows = items.map(item => 
      fieldsToInclude.map(key => fieldMap[key]?.getValue(item) || '')
    );

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `travel_bytes_${format(new Date(), 'yyyy-MM-dd')}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error exporting travel bytes CSV:', error);
    throw error;
  }
};

/**
 * Export travel bytes to JSON format
 */
export const exportTravelBytesToJSON = (items: ShortContentItem[], selectedFields?: Set<string>) => {
  try {
    // Determine which fields to include
    const allFields = [
      'id', 'title', 'content', 'content_type', 'category', 'status',
      'is_featured', 'view_count', 'source', 'slug', 'image_url',
      'publish_date', 'created_at', 'updated_at', 'author_id', 'display_order'
    ];
    
    const fieldsToInclude = selectedFields && selectedFields.size > 0
      ? Array.from(selectedFields)
      : allFields;

    // Create a clean export object
    const exportData = {
      exported_at: new Date().toISOString(),
      total_items: items.length,
      included_fields: fieldsToInclude,
      items: items.map(item => {
        const filteredItem: Record<string, any> = {};
        fieldsToInclude.forEach(field => {
          if (field in item) {
            filteredItem[field] = (item as any)[field];
          }
        });
        return filteredItem;
      })
    };

    // Convert to pretty JSON
    const jsonString = JSON.stringify(exportData, null, 2);

    // Create blob and download
    const blob = new Blob([jsonString], { type: 'application/json' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `travel_bytes_${format(new Date(), 'yyyy-MM-dd')}.json`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error exporting travel bytes JSON:', error);
    throw error;
  }
};
